import api from './api';
import { SettingCategory, SettingScope, SettingType } from '../types/settings';

export interface SystemSetting {
  id: string;
  settingKey: string;
  settingName: string;
  description?: string;
  category: SettingCategory;
  settingType: SettingType;
  scope: SettingScope;
  scopeId?: string;
  value: string;
  defaultValue?: string;
  unit?: string;
  displayOrder: number;
  isRequired: boolean;
  isSensitive: boolean;
  isEditable: boolean;
  requiresRestart: boolean;
  requiresApproval: boolean;
  helpText?: string;
  allowedValues?: string[];
  lastAccessedAt?: Date;
  updatedAt: Date;
}

export interface SettingsFilter {
  category?: SettingCategory;
  scope?: SettingScope;
  scopeId?: string;
  includeInactive?: boolean;
  search?: string;
}

export interface SettingUpdate {
  key: string;
  value: any;
}

export interface CategoryCount {
  category: SettingCategory;
  count: number;
}

class SettingsService {
  private baseUrl = '/api/system/settings';

  /**
   * Get all settings with optional filters
   */
  async getSettings(filters?: SettingsFilter): Promise<{ settings: SystemSetting[]; total: number }> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      });
    }

    const response = await api.get(`${this.baseUrl}?${params.toString()}`);
    return response.data;
  }

  /**
   * Get all setting categories with counts
   */
  async getCategories(): Promise<CategoryCount[]> {
    const response = await api.get(`${this.baseUrl}/categories`);
    return response.data;
  }

  /**
   * Get a specific setting by key
   */
  async getSettingByKey(key: string, scopeId?: string): Promise<SystemSetting> {
    const params = scopeId ? `?scopeId=${scopeId}` : '';
    const response = await api.get(`${this.baseUrl}/${key}${params}`);
    return response.data;
  }

  /**
   * Update a setting value
   */
  async updateSetting(key: string, value: any, scopeId?: string): Promise<SystemSetting> {
    const response = await api.put(`${this.baseUrl}/${key}`, { value, scopeId });
    return response.data;
  }

  /**
   * Bulk update multiple settings
   */
  async bulkUpdateSettings(updates: SettingUpdate[], scopeId?: string): Promise<SystemSetting[]> {
    const response = await api.post(`${this.baseUrl}/bulk`, { updates, scopeId });
    return response.data;
  }

  /**
   * Reset a setting to its default value
   */
  async resetSetting(key: string, scopeId?: string): Promise<SystemSetting> {
    const params = scopeId ? `?scopeId=${scopeId}` : '';
    const response = await api.post(`${this.baseUrl}/reset/${key}${params}`);
    return response.data;
  }

  /**
   * Export all settings for backup
   */
  async exportSettings(category?: SettingCategory, includeSecrets = false): Promise<any> {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (includeSecrets) params.append('includeSecrets', 'true');

    const response = await api.get(`${this.baseUrl}/export/all?${params.toString()}`);
    return response.data;
  }

  /**
   * Transform settings for UI display
   */
  transformSettingsForUI(settings: SystemSetting[]): Record<string, any> {
    const transformed: Record<string, any> = {};

    settings.forEach(setting => {
      // Parse JSON values
      let value: any = setting.value;
      if (setting.settingType === 'json' && typeof value === 'string') {
        try {
          value = JSON.parse(value);
        } catch {
          // Keep as string if parsing fails
        }
      } else if (setting.settingType === 'boolean') {
        value = value === 'true' || value === true;
      } else if (setting.settingType === 'number') {
        value = Number(value);
      }

      // Group by category and create nested structure
      const parts = setting.settingKey.split('.');
      let current = transformed;
      
      for (let i = 0; i < parts.length - 1; i++) {
        if (!current[parts[i]]) {
          current[parts[i]] = {};
        }
        current = current[parts[i]];
      }
      
      current[parts[parts.length - 1]] = value;
    });

    return transformed;
  }

  /**
   * Prepare settings updates from UI data
   */
  prepareUpdatesFromUI(uiData: Record<string, any>, prefix = ''): SettingUpdate[] {
    const updates: SettingUpdate[] = [];

    Object.entries(uiData).forEach(([key, value]) => {
      const fullKey = prefix ? `${prefix}.${key}` : key;

      if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        // Recursively handle nested objects
        updates.push(...this.prepareUpdatesFromUI(value, fullKey));
      } else {
        // Convert value to appropriate format
        let finalValue = value;
        if (typeof value === 'boolean') {
          finalValue = String(value);
        } else if (typeof value === 'object') {
          finalValue = JSON.stringify(value);
        }

        updates.push({ key: fullKey, value: finalValue });
      }
    });

    return updates;
  }
}

export const settingsService = new SettingsService();